import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { User } from "@/lib/api/user";
import { useUpdateUser } from "@/lib/hooks/user/use-update-user";
import { ProfileVideoSection } from "./profile-video-section";
import { UserDetailsCard } from "./user-details-card";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { normalizeRace } from "@/lib/mentor-profile/identity-options";
import {
  raceOptions,
  sexOptions,
  pronounOptions,
} from "@/lib/strings/profile-data";
import { useAbility } from "@/lib/casl/ability-context";
import { hasPrivilegedAccessFromAbility } from "@/lib/casl/privileged-access";

interface UserMentorProfileProps {
  user: User;
}

export function UserMentorProfile({ user }: UserMentorProfileProps) {
  const ability = useAbility();
  const hasPrivilegedAccess = hasPrivilegedAccessFromAbility(ability);
  const canUpdateUsers =
    hasPrivilegedAccess && ability.can("update", "users");
  const canUpdateProfileMedia =
    hasPrivilegedAccess && ability.can("update", "users.profile-media");
  const { handleUpdate, form } = useUpdateUser({
    userId: user.id,
    profile: {
      bio: user.profile?.bio || "",
      sex: user.profile?.sex || "",
      race: normalizeRace(user.profile?.race) || "",
    },
    pronouns: user.pronouns || "",
  });

  const { url: profileVideoSignedUrl } = useSignedMedia(
    user.profile?.video_url ?? undefined,
  );

  return (
    <div className="space-y-6">
      <ProfileVideoSection
        userId={user.id}
        videoUrl={profileVideoSignedUrl}
        disabled={!canUpdateProfileMedia}
      />

      <UserDetailsCard>
        <Form {...form}>
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="pronouns"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-neutral-500">Pronouns</FormLabel>
                  <Select
                    onValueChange={(value) => {
                      handleUpdate("pronouns", value);
                      field.onChange(value);
                    }}
                    defaultValue={field.value}
                    disabled={!canUpdateUsers}
                  >
                    <FormControl>
                      <SelectTrigger className="rounded-xl capitalize">
                        <SelectValue placeholder="Select pronouns" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {pronounOptions.map(({ value, label }) => (
                        <SelectItem
                          key={value}
                          value={value}
                          className="capitalize"
                        >
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="profile.bio"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-neutral-500">Bio</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter bio"
                      className="min-h-[100px] resize-none"
                      {...field}
                      disabled={!canUpdateUsers}
                      onBlur={() => {
                        if (!canUpdateUsers) return;
                        handleUpdate("profile", {
                          ...form.getValues("profile"),
                          bio: field.value,
                        });
                      }}
                    />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="profile.sex"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-neutral-500">Sex</FormLabel>
                  <Select
                    onValueChange={(value) => {
                      handleUpdate("profile", {
                        ...form.getValues("profile"),
                        sex: value,
                      });
                      field.onChange(value);
                    }}
                    defaultValue={field?.value}
                    disabled={!canUpdateUsers}
                  >
                    <FormControl>
                      <SelectTrigger className="rounded-xl capitalize">
                        <SelectValue placeholder="Select biological sex" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {sexOptions.map(({ value, label }) => (
                        <SelectItem
                          key={value}
                          value={value}
                          className="capitalize"
                        >
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="profile.race"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-neutral-500">Race</FormLabel>
                  <Select
                    onValueChange={(value) => {
                      handleUpdate("profile", {
                        ...form.getValues("profile"),
                        race: value,
                      });
                      field.onChange(value);
                    }}
                    defaultValue={field.value}
                    disabled={!canUpdateUsers}
                  >
                    <FormControl>
                      <SelectTrigger className="rounded-xl capitalize">
                        <SelectValue placeholder="Select race" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {raceOptions.map(({ value, label }) => (
                        <SelectItem
                          key={value}
                          value={value}
                          className="capitalize"
                        >
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
          </div>
        </Form>
      </UserDetailsCard>
    </div>
  );
}
